import React, { useState } from 'react';
import { Dropdown } from '@/shared/components/ui';
import { useNavigate } from '@tanstack/react-router';
import { useFinancialReports } from '../hooks/useFinancialReports';
import type { ReportType, ReportTimeRange } from '../types/report.types';

// ---------- Types ----------
interface TimeOption {
  label: string;
  value: ReportTimeRange;
}

interface ReportCardConfig {
  id: ReportType;
  title: string;
  options: TimeOption[];
}

// ---------- Constants ----------
const REPORT_TYPE_CONFIG: Record<
  ReportType,
  { title: string; options: TimeOption[] }
> = {
  PROGRAM_FEES: {
    title: 'Program fees collection report',
    options: [
      { label: 'Monthly', value: 'MONTHLY' },
      { label: 'Quarterly', value: 'QUARTERLY' },
      { label: 'Yearly', value: 'YEARLY' },
    ],
  },
  CUSTOM_CHARGES: {
    title: 'Custom charges summary',
    options: [
      { label: 'Monthly', value: 'MONTHLY' },
      { label: 'Quarterly', value: 'QUARTERLY' },
      { label: 'Yearly', value: 'YEARLY' },
    ],
  },
  OUTSTANDING_BALANCE: {
    title: 'Outstanding balance report',
    options: [
      { label: 'Monthly', value: 'MONTHLY' },
      { label: 'Quarterly', value: 'QUARTERLY' },
      { label: 'Yearly', value: 'YEARLY' },
    ],
  },
};

const REPORT_CARDS: ReportCardConfig[] = Object.entries(REPORT_TYPE_CONFIG).map(
  ([type, config]) => ({
    id: type as ReportType,
    title: config.title,
    options: config.options,
  }),
);

// ---------- Component ----------
export const FinancialReportsGrid: React.FC = () => {
  // Track selected time range per card
  const [timeRanges, setTimeRanges] = useState<
    Record<ReportType, ReportTimeRange>
  >({
    PROGRAM_FEES: 'MONTHLY',
    CUSTOM_CHARGES: 'MONTHLY',
    OUTSTANDING_BALANCE: 'MONTHLY',
  });

  // Handler for time range change
  const handleTimeRangeChange = (
    type: ReportType,
    timeRange: ReportTimeRange,
  ) => {
    setTimeRanges((prev) => ({ ...prev, [type]: timeRange }));
  };

  // Handler for download
  const handleDownload = (reportId: string, link: string) => {
    console.log(`Downloading report: ${reportId}`);
    window.open(link, '_blank');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {REPORT_CARDS.map((card) => (
        <ReportCard
          key={card.id}
          config={card}
          timeRange={timeRanges[card.id]}
          onTimeRangeChange={(timeRange) =>
            handleTimeRangeChange(card.id, timeRange)
          }
          onDownload={handleDownload}
        />
      ))}
    </div>
  );
};

// ---------- Report Card Component ----------
interface ReportCardProps {
  config: ReportCardConfig;
  timeRange: ReportTimeRange;
  onTimeRangeChange: (timeRange: ReportTimeRange) => void;
  onDownload: (reportId: string, link: string) => void;
}

const ReportCard: React.FC<ReportCardProps> = ({
  config,
  timeRange,
  onTimeRangeChange,
  onDownload,
}) => {
  const navigate = useNavigate();

  // Fetch reports for this specific category and time range
  const { data, isLoading, error } = useFinancialReports(config.id, timeRange);

  const reports = data?.data?.reports || [];

  // Handler for navigating to report details
  const handleReportClick = (reportId: string) => {
    navigate({
      to: '/financials/$reportType/$reportId',
      params: {
        reportType: config.id.toLowerCase().replace(/_/g, '-'), // Convert to kebab-case (e.g., program-fees)
        reportId: reportId, // Specific report ID
      },
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-text-primary text-base md:text-xl font-bold">
            {config.title}
          </h3>
          <Dropdown
            options={config.options}
            value={timeRange}
            placeholder=""
            onChange={(val) => onTimeRangeChange(val as ReportTimeRange)}
            className="!w-auto"
            buttonClassName="!min-w-fit !gap-4 text-text-secondary"
            optionsClassNames="!text-[16px]"
            labelClassName="!text-[16px]"
          />
        </div>
        <div className="bg-white rounded-lg border border-[#E6E6E6] p-8 flex items-center justify-center">
          <p className="text-text-secondary">Loading reports...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-text-primary text-base md:text-xl font-bold">
            {config.title}
          </h3>
          <Dropdown
            options={config.options}
            value={timeRange}
            placeholder=""
            onChange={(val) => onTimeRangeChange(val as ReportTimeRange)}
            className="!w-auto"
            buttonClassName="!min-w-fit !gap-4 text-text-secondary"
            optionsClassNames="!text-[16px]"
            labelClassName="!text-[16px]"
          />
        </div>
        <div className="bg-white rounded-lg border border-[#E6E6E6] p-8 flex items-center justify-center">
          <p className="text-red-500">Failed to load reports</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!reports.length) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-text-primary text-base md:text-xl font-bold">
            {config.title}
          </h3>
          <Dropdown
            options={config.options}
            value={timeRange}
            placeholder=""
            onChange={(val) => onTimeRangeChange(val as ReportTimeRange)}
            className="!w-auto"
            buttonClassName="!min-w-fit !gap-4 text-text-secondary"
            optionsClassNames="!text-[16px]"
            labelClassName="!text-[16px]"
          />
        </div>
        <div className="bg-white rounded-lg border border-[#E6E6E6] p-8 flex items-center justify-center">
          <p className="text-text-secondary">No reports available</p>
        </div>
      </div>
    );
  }

  // Success state with reports
  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-text-primary text-base md:text-xl font-bold">{config.title}</h3>
        <Dropdown
          options={config.options}
          value={timeRange}
          placeholder=""
          onChange={(val) => onTimeRangeChange(val as ReportTimeRange)}
          className="!w-auto"
          buttonClassName="!min-w-fit !gap-4 text-text-secondary"
          optionsClassNames="!text-[16px]"
          labelClassName="!text-[16px]"
        />
      </div>

      {/* Reports */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E6E6E6]">
        {reports.map((report) => (
          <div
            key={report.id}
            className="flex items-center justify-between hover:bg-gray-50 transition border-b border-[#E6E6E6] last:border-b-0"
          >
            <button
              onClick={() => handleReportClick(report.id)}
              className="text-text-secondary p-4 w-[90%] text-left hover:text-primary transition-colors cursor-pointer"
            >
              {report.title}
            </button>
            <button
              onClick={() => onDownload(report.id, report.link)}
              className="cursor-pointer p-4 border-l border-[#E6E6E6] w-[10%] h-full hover:bg-gray-100 transition"
              aria-label={`Download ${report.title}`}
            >
              <svg
                className="h-[23px] w-[22px]"
                width="21"
                height="20"
                viewBox="0 0 21 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.83203 13.3332L3.83203 14.1665C3.83203 15.5472 4.95132 16.6665 6.33203 16.6665L14.6654 16.6665C16.0461 16.6665 17.1654 15.5472 17.1654 14.1665L17.1654 13.3332M13.832 9.99984L10.4987 13.3332M10.4987 13.3332L7.16536 9.99984M10.4987 13.3332L10.4987 3.33317"
                  stroke="#141414"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
